import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { request } from "../../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import Loading from "../utilComponent/Loading";
import NotFoundTitle from "../utilComponent/NotFoundTitle";
import Pagination from "../utilComponent/Pagination";
import { showImage } from "../../../utils";

const AssignProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [members, setMembers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCards, setSelectedCards] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  const getProductList = async (page = 1) => {
    try {
      setLoading(true);
      const response = await request({
        url: `products?page=${page}&per_page=${pagination.per_page}`,
        method: "GET",
      });
      if (response?.status) {
        setProducts(response.data?.items || []);
        setPagination(response.data?.pagination || pagination);
      }
    } finally {
      setLoading(false);
    }
  };

  const getMembers = async () => {
    const response = await request({
      url: "warehouse-members",
      method: "GET",
    });
    setMembers(response?.data || []);
  };

  useEffect(() => {
    getMembers();
    if (id) {
      getAssignProductById(pagination.current_page);
      setSelectedUser(id);
    } else {
      getProductList(pagination.current_page);
    }
  }, [id]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    setSelectAll(false);

    if (id) {
      getAssignProductById(page);
      return;
    }

    getProductList(page);
  };

  const toggleCard = (id) => {
    setSelectedCards((prev) => {
      if (prev[id] !== undefined) {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      }
      return { ...prev, [id]: "" };
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedCards({});
      setSelectAll(false);
    } else {
      const all = {};
      products.forEach((p) => (all[p.id] = ""));
      setSelectedCards(all);
      setSelectAll(true);
    }
  };

  const handlePriceChange = (id, value) => {
    setSelectedCards((prev) => ({ ...prev, [id]: value }));
  };

  const validateBeforeSubmit = () => {
    const entries = Object.entries(selectedCards);

    if (entries.length === 0) {
      toast.error("Please select at least one product");
      return false;
    }

    const invalid = entries.find(
      ([, price]) => price === "" || Number(price) <= 0,
    );

    if (invalid) {
      toast.error("Please enter valid price for all selected products");
      return false;
    }

    if (!selectedUser) {
      toast.error("Please select a user");
      return false;
    }

    return true;
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append("user_id", selectedUser);

    Object.entries(selectedCards).forEach(([productId, price], index) => {
      fd.append(`products[${index}][product_id]`, productId);
      fd.append(`products[${index}][price]`, price);
    });

    return fd;
  };

  const assignProducts = async () => {
    if (!validateBeforeSubmit()) return;

    if (id) {
      await updateAssignProduct();
    } else {
      await addAssignProduct();
    }
  };

  const addAssignProduct = async () => {
    try {
      setPostLoading(true);
      await request({
        url: "assign-products",
        method: "POST",
        data: buildFormData(),
      });
      toast.success("Product Assign Successfully!");
      setSelectedCards({});
      setSelectAll(false);
    } catch {
      toast.error("Something went wrong");
    } finally {
      setPostLoading(false);
    }
  };

  const updateAssignProduct = async () => {
    try {
      setPostLoading(true);
      await request({
        url: `assign-products/${id}`,
        method: "POST",
        data: buildFormData(),
      });
      toast.success("Product Assign Update Successfully!");
      setSelectedCards({});
      setSelectAll(false);
      navigate("/assign-product-list");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setPostLoading(false);
    }
  };

  const getAssignProductById = async (page = 1) => {
    try {
      setLoading(true);

      const response = await request({
        url: `assign-products/${id}?page=${page}&per_page=${pagination.per_page}`,
        method: "GET",
      });

      if (response.status && response.data) {
        const items = response.data?.assigned_products?.items || [];
        const paginationData =
          response.data?.assigned_products?.pagination ||
          response.data?.pagination ||
          pagination;

        setProducts(items);
        setPagination(paginationData);
        const prefill = {};
        items.forEach((p) => {
          prefill[p.id] = p.price ?? ""; // "2.00"
        });
        setSelectedCards(prefill);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="container-fluid p-0">
        <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
          <div className="container-fluid">
            <ol className="breadcrumb bg-transparent mb-0">
              <li className="breadcrumb-item">
                <Link className="text-secondary" to="/hrms">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item text-secondary">Assign</li>
              <li className="breadcrumb-item active">AssignProduct</li>
            </ol>
          </div>
        </div>

        <div className="card mx-4">
          <div className="row px-4 my-3">
            <div className="col-md-4">
              <label className="form-label fw-bold">Select User</label>
              <select
                className="form-select"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                disabled={!!id}
              >
                <option value="">Choose user</option>
                {members.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3 d-flex align-items-end">
              <button
                className="btn btn-warning w-100"
                onClick={toggleSelectAll}
              >
                {selectAll ? "Deselect All" : "Select All"}
              </button>
            </div>
          </div>

          <hr />

          {loading ? (
            <Loading />
          ) : (
            <>
              <div className="row g-3 px-4 mt-2">
                {products?.length === 0 ? (
                  <NotFoundTitle />
                ) : (
                  products?.map((p) => {
                    const isSelected = selectedCards[p.id] !== undefined;
                    const invalidPrice = isSelected && !selectedCards[p.id];

                    return (
                      <div className="col-3" key={p.id}>
                        <div
                          className="card"
                          style={{
                            border: isSelected
                              ? "3px solid green"
                              : "1px solid #ccc",
                          }}
                        >
                          <img
                            className="card-img-top"
                            src={showImage(p.images[0]?.url)}
                            alt={p.name}
                            style={{ height: 220, objectFit: "cover" }}
                          />

                          <div className="card-body">
                            <h5 className="card-title">{p.name}</h5>
                            <p className="mb-1">
                              <strong>Quantity:</strong> {p.quantity}
                            </p>
                            <p className="mb-3">
                              <strong>SKU:</strong> {p.sku}
                            </p>

                            <input
                              type="number"
                              className={`form-control mb-2 ${
                                invalidPrice ? "border border-danger" : ""
                              }`}
                              placeholder="Enter price"
                              value={selectedCards[p.id] ?? ""}
                              disabled={!isSelected}
                              onChange={(e) =>
                                handlePriceChange(p.id, e.target.value)
                              }
                            />

                            <button
                              className={`btn w-100 ${
                                isSelected ? "btn-danger" : "btn-success"
                              }`}
                              onClick={() => toggleCard(p.id)}
                            >
                              {isSelected ? "Deselect Card" : "Select Card"}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {pagination.last_page > 1 && (
                <Pagination
                  currentPage={pagination.current_page}
                  lastPage={pagination.last_page}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}

          <div className="card-footer mt-4">
            <button
              className="btn btn-primary ms-4"
              onClick={assignProducts}
              disabled={postLoading}
            >
              {postLoading
                ? "Saving..."
                : id
                  ? "Update Assigned Products"
                  : "Save Selected Cards"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignProduct;
